import axios from "axios";
import {
  API_BASE_URL,
  getCurrentAdminApiKey,
  getDefaultHeaders,
} from "../config";
import { logoutUser } from "../../state/redux/auth/authSlice";
import { store } from "../../state/redux/store";
import {
  shouldWaitForAuthRefresh,
  waitForAuthRefresh,
} from "./authRefreshGate";
import {
  applyRefreshedAuthTokenFromResponse,
  hasExhaustedApiKeyRetry,
  isApiKeyExpiredError,
  isInactivityTimeoutError,
  retryRequestWithFreshAdminApiKey,
  retryRequestWithLatestAuthToken,
  shouldAttemptApiKeyRefresh,
  shouldRedirectToLoginForApiError,
  shouldSkipApiKeyRefresh,
} from "./authRetryUtils";
import { ensureFreshToken } from "./tokenRefreshManager";
import {
  applyApiSchemeToConfig,
  applyAssetSchemeToResponse,
  handleApiNetworkFailure,
} from "../httpAccess";
import { getLoginPath } from "../authPortal";

let navigateRef = null;
let isLoggingOut = false;

const handleUnauthorized = () => {
  if (!navigateRef || isLoggingOut) {
    return;
  }

  isLoggingOut = true;
  const loginPath = getLoginPath();
  const userLang = localStorage.getItem("language") || "ar";
  const dir = userLang === "ar" || userLang === "العربية" ? "rtl" : "ltr";

  document.documentElement.setAttribute("dir", dir);
  document.documentElement.setAttribute("lang", userLang);

  store.dispatch(logoutUser()).finally(() => {
    isLoggingOut = false;
    navigateRef(loginPath, {
      state: { sessionExpired: true, lang: userLang },
      replace: true,
    });
  });
};

const handleAxiosError = async (error) => {
  applyRefreshedAuthTokenFromResponse(error?.response);

  if (error?.config?._tokenRefreshProbe) {
    return Promise.reject(error);
  }

  if (error?.response?.status === 401 && !isInactivityTimeoutError(error)) {
    const retryResponse = await retryRequestWithLatestAuthToken(
      error,
      axiosInstance,
    );
    if (retryResponse) {
      return retryResponse;
    }
  }

  if (shouldAttemptApiKeyRefresh(error) && !shouldSkipApiKeyRefresh(error)) {
    try {
      const retryResponse = await retryRequestWithFreshAdminApiKey(
        error,
        axiosInstance,
      );
      if (retryResponse) {
        return retryResponse;
      }
    } catch (retryError) {
      if (
        retryError?.response?.status === 401 &&
        !isInactivityTimeoutError(retryError)
      ) {
        const authRetryResponse = await retryRequestWithLatestAuthToken(
          retryError,
          axiosInstance,
        );
        if (authRetryResponse) {
          return authRetryResponse;
        }
      }
      error = retryError;
    }
  }

  if (error && error.response) {
    const { status } = error.response;
    const apiKeyRecoveryExhausted =
      isApiKeyExpiredError(error) && hasExhaustedApiKeyRetry(error);

    if (
      (status === 401 || status === 403 || status === 429) &&
      !error.config?._skipAuthRedirect &&
      shouldRedirectToLoginForApiError(error) &&
      (!isApiKeyExpiredError(error) || apiKeyRecoveryExhausted)
    ) {
      handleUnauthorized();
    } else if (status === 405 && navigateRef) {
      navigateRef("/notfound");
    }
  }

  return Promise.reject(error);
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: getDefaultHeaders(),
});

axiosInstance.interceptors.request.use(
  async (config) => {
    if (shouldWaitForAuthRefresh(config)) {
      await waitForAuthRefresh();
    }

    const token = localStorage.getItem("authToken");
    const adminApiKey = getCurrentAdminApiKey();

    config.headers = config.headers || {};

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (adminApiKey) {
      config.headers["ADMIN-API-KEY"] = adminApiKey;
    }

    return applyApiSchemeToConfig(config);
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) =>
    applyAssetSchemeToResponse(applyRefreshedAuthTokenFromResponse(response)),
  (error) => {
    handleApiNetworkFailure(error);
    return handleAxiosError(error);
  },
);

const setupInterceptors = (navigate) => {
  navigateRef = navigate;
};

const ensureFreshAuthToken = (options) =>
  ensureFreshToken(axiosInstance, options);

export { axiosInstance, setupInterceptors, ensureFreshAuthToken };
